// src/pages/ProcessPage.js
import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useColorModeValue,
  Button,
  Circle,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// The data for our process steps
const processData = [
  {
    step: '01',
    title: 'Consultation & Planning',
    text: 'This foundational phase is where we align with your vision. We conduct in-depth site analyses, develop a detailed project scope, establish clear budgets and timelines, and handle all necessary pre-construction approvals. Our goal is to create a comprehensive and strategic plan that minimizes risks and sets the stage for a successful build.',
  },
  {
    step: '02',
    title: 'Design & Construction',
    text: 'With a solid plan in place, our architects and engineers finalize the blueprints while our construction team mobilizes. We manage all aspects of the build, from procurement to site management, with a relentless focus on safety and craftsmanship. You will receive regular progress updates, ensuring you are informed every step of the way.',
  },
  {
    step: '03',
    title: 'Final Inspection & Handover',
    text: 'As construction concludes, we conduct a meticulous quality inspection covering every detail of the project. We then guide you through a final walkthrough to ensure every aspect meets your complete satisfaction. Upon approval, we officially hand over the keys and all project documentation, delivering a finished structure that is ready for you to occupy.',
  },
];

// A new, reusable component for the timeline layout
const TimelineStep = ({ step, title, text, isLeft }) => {
  const content = (
    <VStack
      bg={useColorModeValue('gray.100', 'gray.700')}
      p={8}
      borderRadius="xl"
      align="flex-start"
      spacing={4}
      w="full"
      boxShadow="md"
    >
      <Heading as="h3" size="lg">{title}</Heading>
      <Text color="gray.500" lineHeight="tall">{text}</Text>
    </VStack>
  );

  const spacer = <Box flex="1" />;

  return (
    <Flex
      w="full"
      align="center"
      justify="space-between"
      position="relative"
      gap={10}
    >
      {isLeft ? content : spacer}
      
      {/* The styled node on the timeline */}
      <Circle
        size="80px"
        bg="orange.500"
        color="white"
        fontSize="2xl"
        fontWeight="bold"
        zIndex="1"
        boxShadow="xl"
      >
        {step}
      </Circle>
      
      {isLeft ? spacer : content}
    </Flex>
  );
};

export default function ProcessPage() {
  const bgColor = useColorModeValue('white', 'gray.800');
  const lineColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <>
      <Header />
      <Box as="main" bg={bgColor}>
        <VStack maxW="7xl" mx="auto" px={8} spacing={12} py={{ base: 16, md: 24 }}>
          {/* Page Header */}
          <VStack spacing={4} textAlign="center">
            <Breadcrumb>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink href="#">Our Process</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
            <Heading as="h1" size="2xl" fontWeight="extrabold">
              Our Proven Process
            </Heading>
            <Text maxW="3xl" color="gray.600">
              Our streamlined, three-step process ensures transparency, efficiency, and exceptional quality from the initial concept to the final handover.
            </Text>
          </VStack>

          {/* The Timeline Container */}
          <VStack
            spacing={20}
            position="relative"
            // This pseudo-element creates the central vertical line
            _before={{
              content: '""',
              position: 'absolute',
              top: '40px',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              bg: lineColor,
              borderRadius: 'full',
            }}
          >
            {processData.map((item, index) => (
              <TimelineStep
                key={item.step}
                step={item.step}
                title={item.title}
                text={item.text}
                // Determine if the content should be on the left or right
                isLeft={index % 2 === 0}
              />
            ))}
          </VStack>

          {/* Call to Action */}
          <VStack spacing={6} textAlign="center" py={12}>
            <Heading as="h2" size="xl">Ready to Start Your Project?</Heading>
            <Text maxW="2xl">
              Let our proven process work for you. Contact us today to schedule your initial consultation.
            </Text>
            <Button as={RouterLink} to="/contact" colorScheme="orange" size="lg">
              Get In Touch
            </Button>
          </VStack>
        </VStack>
      </Box>
      <Footer />
    </>
  );
}